import { ICellRendererParams } from '@ag-grid-community/core';

import { Organization } from '../../../../organization/models/organization';
import { UserDatacenterView } from '../../../models/datacenter-aggrid-view';
import { TranslationService } from '../../../services/translation.service';
import { MultiLineValueTypes } from '../../cell-renderers/multi-line-value-types';
import {
	CommonColDef,
	LinkColDef,
	MultiLineColDef,
} from '../../column-defs';
import { ColumnType } from '../../column-type';
import { DatacenterTextFilterComponent } from '../../filter-type/datacenter-text-filter.component';
import { DatacenterNumberFilterComponent } from '../../filter-type/datacenter-number-filter.component';
import { DatacenterDropdownFilterComponent } from '../../filter-type/datacenter-dropdown-filter.component';
import { DatacenterExactNumberFilterComponent } from '../../filter-type/datacenter-exact-number-filter.component';

function formatColumnValue(colId: string, organization: Organization | undefined) {
	if (!colId || organization === undefined) {
		return '';
	}

	switch (colId) {
	case 'suppliers':
		organization.suppliersString = organization.suppliers
			? organization.suppliers.map(item => item.name).join(';') : '';
		return organization.suppliersString;
	case 'city':
		organization.city = organization.address.city;
		return organization.city;
	case 'country':
		if (organization.address.countryCode) {
			return organization.address.countryCodeDescription;
		}
		break;
	case 'role':
		return organization.roleDescription;
	case 'status':
		return organization.statusDescription;
	}
	return '';
}
const linkToOrgCardCellRendererParams = {
	routerLink(params: ICellRendererParams<Organization, unknown>) {
		if (!params.data) {
			return null;
		}
		const { data } = params;
		return {
			commands: [
				'/user',
				'organization',
				data.id,
				'details',
			]
		};
	}
};
export class OrganizationDatacenterColumnDefinition {
	readonly ORGANIZATION_REQUIRED_FIELDS = ['name'];

	readonly ORGANIZATION_DATA_CENTER_DEFAULT_CONFIGURATION: Map<string, CommonColDef<Organization>>;

	constructor(private readonly translationService: TranslationService) {
		this.ORGANIZATION_DATA_CENTER_DEFAULT_CONFIGURATION = new
		Map<string, CommonColDef<Organization>>([
			[
				'id',
				new LinkColDef<Organization>({
					headerName: this.translationService.getTR('datacenter-filter.organization-id'),
					field: 'id',
					colId: 'id',
					filter: DatacenterExactNumberFilterComponent,
					suppressHeaderMenuButton: false,
					cellRendererParams: linkToOrgCardCellRendererParams,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => params.data ? params.data.id : ''
				})
			],
			[
				'name',
				new CommonColDef<Organization>({
					headerName: this.translationService.getTR('datacenter-filter.organization-name'),
					field: 'name',
					colId: 'name',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'role',
				new CommonColDef<Organization>({
					headerName: this.translationService.getTR('datacenter-filter.organization-type'),
					colId: 'role',
					field: 'role',
					filter: DatacenterDropdownFilterComponent,
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN,
					valueGetter: params => formatColumnValue('role', params.data),
				})
			],
			[
				'address.countryCode',
				new MultiLineColDef<Organization>({
					headerName: this.translationService.getTR('organization.card.identity.country'),
					colId: 'address.countryCode',
					valueGetter: params => formatColumnValue('country', params.data),
					field: 'address.countryCode',
					minWidth: 150,
					filter: DatacenterDropdownFilterComponent,
					filterParams: { organizationFields: ['address.countryCode', 'address.countryCodeDescription'] },
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TRANSLATED_COLUMN,
					valueType: MultiLineValueTypes.TEXT
				})
			],
			[
				'city',
				new CommonColDef<Organization>({
					headerName: this.translationService.getTR('datacenter-filter.organization-city'),
					field: 'address.city',
					valueGetter: params => formatColumnValue('city', params.data),
					filterParams: { organizationFields: ['address.city', 'address.city'] },
					colId: 'city',
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'status',
				new CommonColDef<Organization>({
					headerName: this.translationService.getTR('datacenter-filter.organization-status'),
					field: 'status',
					colId: 'status',
					minWidth: 100,
					valueGetter: params => formatColumnValue('status', params.data),
					filter: DatacenterTextFilterComponent,
					suppressHeaderMenuButton: false,
					sortable: false,
					columnType: ColumnType.TEXT_COLUMN
				})
			],
			[
				'suppliersString',
				new MultiLineColDef<Organization>({
					headerName: this.translationService.getTR('datacenter-filter.organization-suppliers'),
					colId: 'suppliersString',
					valueGetter: params => formatColumnValue('suppliers', params.data),
					minWidth: 150,
					filter: DatacenterNumberFilterComponent,
					suppressHeaderMenuButton: true,
					columnType: ColumnType.TEXT_COLUMN,
					valueType: MultiLineValueTypes.TEXT
				})
			],
		]);
	}
}

export const DEFAULT_VIEW_ORGANIZATION_DATACENTER: UserDatacenterView = {
	columnState: [
		{
			colId: 'id',
			pinned: 'left'
		},
		{
			colId: 'name',
			pinned: 'left'
		},
		{
			colId: 'role',
			pinned: 'left'
		},
		{
			colId: 'address.countryCode',
			pinned: 'left'
		},
		{
			colId: 'city',
			pinned: 'left'
		},
		{
			colId: 'status',
			pinned: 'left'
		},
		{
			colId: 'suppliersString'
		}
	],
	sortModel: [
		{
			colId: 'name',
			sort: 'asc'
		}
	],
	filterState: [],
	type: 'organization',
	isDefault: true,
	name: 'Default',
	id: -99
};
